/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publicAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import {
  Building2,
  Users,
  Crown,
  Shield,
  ChevronRight,
  LogIn,
  LayoutDashboard,
  User,
  Briefcase,
  ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.45, ease: 'easeOut' } }),
};

const ROLE_LABELS = {
  admin: 'Sistem Yoneticisi',
  genel_sekreter: 'Genel Sekreter',
  kent_konseyi_baskani: 'Kent Konseyi Başkanı',
  meclis_baskani: 'Meclis Başkanı',
  vatandas: 'Vatandas',
  muhtar: 'Muhtar',
  stk_temsilcisi: 'STK Temsilcisi',
};

const PersonCard = ({ person, title, size = 'md', index = 0 }) => {
  if (!person) return null;
  const initials = `${(person.name || '')[0] || ''}${(person.surname || '')[0] || ''}`.toUpperCase();
  const avatarUrl = person.avatar_url ? `${API_URL}${person.avatar_url}` : null;

  const sizes = {
    lg: { card: 'p-8', avatar: 'w-28 h-28 text-3xl', name: 'text-xl', title: 'text-sm' },
    md: { card: 'p-6', avatar: 'w-20 h-20 text-xl', name: 'text-base', title: 'text-xs' },
    sm: { card: 'p-5', avatar: 'w-16 h-16 text-lg', name: 'text-sm', title: 'text-xs' },
  };
  const s = sizes[size];

  return (
    <motion.div variants={fadeUp} custom={index}
      className={`bg-white rounded-2xl border border-slate-200 ${s.card} flex flex-col items-center text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300`}
      data-testid={`person-card-${person.id}`}>
      {avatarUrl ? (
        <img src={avatarUrl} alt={`${person.name} ${person.surname}`}
          className={`${s.avatar} rounded-full object-cover border-4 border-slate-100 mb-4`} />
      ) : (
        <div className={`${s.avatar} rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center font-semibold border-4 border-slate-100 mb-4`}>
          {initials}
        </div>
      )}
      <h3 className={`${s.name} font-semibold text-slate-900`} style={{ fontFamily: "'Outfit', sans-serif" }}>
        {person.name} {person.surname}
      </h3>
      <p className={`${s.title} text-blue-600 font-medium mt-1`}>{title || ROLE_LABELS[person.role] || person.role}</p>
    </motion.div>
  );
};

const SectionHeader = ({ icon: Icon, title, subtitle, color = 'blue' }) => {
  const colors = {
    blue: 'from-blue-600 to-blue-700',
    emerald: 'from-emerald-600 to-emerald-700',
    amber: 'from-amber-500 to-amber-600',
    purple: 'from-purple-600 to-purple-700',
    slate: 'from-slate-700 to-slate-800',
  };
  return (
    <motion.div variants={fadeUp} className="flex items-center gap-4 mb-8">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <h2 className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </motion.div>
  );
};

const KurumsalPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.getKurumsal()
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { konsey_adi, baskan, genel_sekreter, genel_sekreter_yardimcisi, yurutme_kurulu, meclisler } = data || {};
  const konseyName = konsey_adi || 'Kent Konseyi';

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200" data-testid="kurumsal-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="kurumsal-logo">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-slate-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Kent Konseyi</p>
              <p className="text-xs text-slate-500 -mt-0.5">Dijital Katılım</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Anasayfa</Link>
            <Link to="/kurumsal" className="text-sm text-blue-600 font-medium">Kurumsal</Link>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate('/panel')} className="h-9 gap-2 bg-blue-600 hover:bg-blue-700 rounded-full px-5" data-testid="kurumsal-panel-btn">
                <LayoutDashboard className="w-4 h-4" /> Panele Git
              </Button>
            ) : (
              <Button onClick={() => navigate('/giris')} className="h-9 gap-2 bg-blue-600 hover:bg-blue-700 rounded-full px-5" data-testid="kurumsal-login-btn">
                <LogIn className="w-4 h-4" /> Giriş Yap
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="relative pt-16">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" animate="visible">
              <motion.div variants={fadeUp} custom={0}>
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white/80 transition-colors mb-6">
                  <ArrowLeft className="w-4 h-4" /> Anasayfaya Dön
                </Link>
              </motion.div>
              <motion.div variants={fadeUp} custom={1}>
                <Badge className="bg-white/10 text-white/90 border-white/20 backdrop-blur-sm mb-4 text-xs tracking-wider uppercase px-3 py-1">
                  Kurumsal Yapı
                </Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} custom={2}
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tighter leading-tight mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}>
                {konseyName}<br />
                <span className="text-blue-400">Yönetim Kadrosu</span>
              </motion.h1>
              <motion.p variants={fadeUp} custom={3} className="text-lg text-white/60 max-w-xl">
                Kent Konseyi yönetim organları, meclis başkanları ve yürütme kurulu üyelerini tanıyın.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">

        {/* === Kent Konseyi Baskani & Genel Sekreter & Yardimcisi === */}
        {(baskan || genel_sekreter || genel_sekreter_yardimcisi) && (
          <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} data-testid="leadership-section">
            <SectionHeader icon={Crown} title={konseyName} subtitle="Kent Konseyi Başkanı, Genel Sekreter ve Genel Sekreter Yardımcısı" color="slate" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl">
              {baskan && <PersonCard person={baskan} title="Kent Konseyi Başkanı" size="lg" index={0} />}
              {genel_sekreter && <PersonCard person={genel_sekreter} title="Genel Sekreter" size="lg" index={1} />}
              {genel_sekreter_yardimcisi && <PersonCard person={genel_sekreter_yardimcisi} title="Genel Sekreter Yardımcısı" size="lg" index={2} />}
            </div>
          </motion.section>
        )}

        {/* === Yurutme Kurulu === */}
        {yurutme_kurulu && (
          <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} data-testid="yurutme-kurulu-section">
            <SectionHeader icon={Shield} title={yurutme_kurulu.name} subtitle="Kent Konseyi ana yurutme organi" color="amber" />
            {yurutme_kurulu.baskan && (
              <div>
                <p className="text-xs tracking-widest uppercase text-slate-400 font-semibold mb-3">Başkan</p>
                <div className="max-w-xs">
                  <PersonCard person={yurutme_kurulu.baskan} title="Yürütme Kurulu Başkanı" size="md" index={0} />
                </div>
              </div>
            )}
            {!yurutme_kurulu.baskan && (
              <div className="p-6 rounded-2xl border border-dashed border-slate-200 text-center bg-white">
                <p className="text-sm text-slate-400">Henüz Yürütme Kurulu Başkanı atanmamış</p>
              </div>
            )}
            {yurutme_kurulu.uyeler && yurutme_kurulu.uyeler.length > 0 && (
              <div className="mt-10">
                <p className="text-xs tracking-widest uppercase text-slate-400 font-semibold mb-3">Üyeler ({yurutme_kurulu.uyeler.length})</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {yurutme_kurulu.uyeler.map((u, idx) => (
                    <PersonCard key={u.id} person={u} title="Yürütme Kurulu Üyesi" size="sm" index={idx} />
                  ))}
                </div>
              </div>
            )}
          </motion.section>
        )}

        {/* === Meclisler === */}
        {meclisler && meclisler.length > 0 && (
          <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} data-testid="meclisler-section">
            <SectionHeader icon={Building2} title="Meclisler" subtitle={`${meclisler.length} aktif meclis`} color="blue" />
            <div className="space-y-16">
              {meclisler.map((meclis, mi) => (
                <motion.div key={meclis.id} variants={fadeUp} custom={mi}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
                  data-testid={`meclis-${meclis.id}`}>

                  {/* Meclis Header */}
                  <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 md:px-8 py-6">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{meclis.name}</h3>
                        {meclis.description && <p className="text-sm text-white/50 mt-0.5">{meclis.description}</p>}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 md:p-8 space-y-8">
                    {/* Meclis Baskani */}
                    {meclis.baskan && (
                      <div>
                        <p className="text-xs tracking-widest uppercase text-blue-600 font-semibold mb-4">Meclis Başkanı</p>
                        <div className="max-w-xs">
                          <PersonCard person={meclis.baskan} title={`${meclis.name} Başkanı`} size="md" index={0} />
                        </div>
                      </div>
                    )}

                    {/* Meclis Yurutme Kurulu Baskani */}
                    {meclis.yurutme_kurulu && meclis.yurutme_kurulu.baskan && (
                      <div>
                        <p className="text-xs tracking-widest uppercase text-amber-600 font-semibold mb-4">
                          <Shield className="w-3.5 h-3.5 inline mr-1.5" />
                          {meclis.yurutme_kurulu.name} Başkanı
                        </p>
                        <div className="max-w-xs">
                          <PersonCard person={meclis.yurutme_kurulu.baskan} title="YK Başkanı" size="sm" index={0} />
                        </div>
                      </div>
                    )}

                    {/* Yonetim Kurulu Uyeleri */}
                    {meclis.yonetim_kurulu_uyeleri && meclis.yonetim_kurulu_uyeleri.length > 0 && (
                      <div>
                        <p className="text-xs tracking-widest uppercase text-slate-500 font-semibold mb-4">
                          <Users className="w-3.5 h-3.5 inline mr-1.5" />
                          Yönetim Kurulu Üyeleri ({meclis.yonetim_kurulu_uyeleri.length})
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                          {meclis.yonetim_kurulu_uyeleri.map((m, i) => (
                            <PersonCard key={m.id} person={m} title="YK Üyesi" size="sm" index={i} />
                          ))}
                        </div>
                      </div>
                    )}

                    {!meclis.baskan && (!meclis.yurutme_kurulu || !meclis.yurutme_kurulu.baskan) && (
                      <div className="text-center py-8">
                        <User className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-400">Henüz üye atanmamış</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 py-12" data-testid="kurumsal-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <p className="text-sm text-white/60">Adana Kent Konseyi Dijital Katılım Platformu</p>
          </div>
          <p className="text-xs text-white/30">2026 Tüm hakları saklıdır.</p>
        </div>
      </footer>
    </div>
  );
};

export default KurumsalPage;
;
